/* assets/cbi-cart.js
 * Cart drawer and cart page, on the Cart AJAX API and the Section Rendering
 * API.
 *
 * Theme settings, Cart (data-cbi-cart-type on <body>):
 * - drawer: Add to cart stays on the page, adds the piece and opens the
 *   drawer; the header's Cart link opens the drawer too (except on the cart
 *   page itself).
 * - page: Add to cart posts normally and lands on /cart.
 *
 * Everywhere: Remove links, cart line steppers and the order note update the
 * cart in place, and every [data-cbi-cart-root] on the page (the drawer, the
 * cart page) is re-rendered from its section. After each change a
 * cbi:cart:updated event lets the header refresh its count, and product buy
 * areas ([data-cbi-variant]) are set against what is left: the stepper shows
 * while two or more can still be added, and Add to cart gives way to In cart
 * once the cart holds the lot. In cart ([data-cbi-cart-open]) opens the
 * drawer like the header's Cart link. Without JavaScript every control is a
 * plain link or form.
 *
 * Screen readers hear each change through a polite live region. In the theme
 * editor, selecting the Cart drawer section opens the drawer.
 */

(() => {
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const body = document.body;
  const useDrawer = () => body.dataset.cbiCartType !== 'page' && !!document.querySelector('[data-cbi-cart-drawer]');
  const onCartPage = () => body.classList.contains('cbi-template-cart');

  const drawer = () => document.querySelector('[data-cbi-cart-drawer]');

  const sectionIds = () =>
    Array.from(document.querySelectorAll('[data-cbi-cart-root]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  /* Replace each cart root with its freshly rendered counterpart. Focus that
   * was inside a replaced root (the drawer's Close button, a Remove link)
   * goes to the root's first control rather than falling back to the page. */
  const render = (sections) => {
    if (!sections) return;
    for (const el of document.querySelectorAll('[data-cbi-cart-root]')) {
      const html = sections[el.dataset.sectionId];
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cbi-cart-root]');
      if (!fresh) continue;
      const hadFocus = el.contains(document.activeElement);
      el.innerHTML = fresh.innerHTML;
      if (hadFocus) {
        const target = el.querySelector('[data-cbi-cart-close], a[href], button, textarea');
        if (target) target.focus();
      }
    }
  };

  /* The ends of a stepper go dead at the ends of its range. */
  const syncSteps = (stepper) => {
    const input = stepper.querySelector('.cbi-qty__input');
    if (!input) return;
    const value = Number(input.value || 1);
    for (const button of stepper.querySelectorAll('[data-cbi-qty-step]')) {
      const next = value + Number(button.dataset.cbiQtyStep);
      button.disabled = next < Number(input.min || 1) || next > Number(input.max || 99);
    }
  };

  /* Hold a product stepper to what is left: hidden below two, and never
   * offering more than the stock the cart has not already taken. */
  const setRemaining = (buy, remaining, reset) => {
    buy.dataset.inCart = String(remaining <= 0);
    const stepper = buy.querySelector('[data-cbi-qty]');
    const input = stepper && stepper.querySelector('.cbi-qty__input');
    if (!stepper || !input) return;
    stepper.hidden = remaining < 2;
    input.max = String(Math.max(remaining, 1));
    if (reset || Number(input.value) > remaining) input.value = '1';
    syncSteps(stepper);
  };

  const syncBuy = (cart) => {
    const held = new Map();
    for (const item of cart.items || []) {
      const id = String(item.variant_id);
      held.set(id, (held.get(id) || 0) + item.quantity);
    }
    for (const el of document.querySelectorAll('[data-cbi-variant]')) {
      const stock = Number(el.dataset.cbiStock || 0);
      setRemaining(el, Math.max(stock - (held.get(el.dataset.cbiVariant) || 0), 0), false);
    }
  };

  /* Settle every buy area against the cart as the server has it. */
  const syncFromServer = () => {
    if (!document.querySelector('[data-cbi-variant]')) return Promise.resolve();
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => cart && syncBuy(cart))
      .catch(() => {});
  };

  /* One polite live region for the whole page, created on first use. */
  let liveRegion = null;
  const say = (message) => {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'cbi-visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      document.body.append(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  };

  const announce = () => {
    document.dispatchEvent(new CustomEvent('cbi:cart:updated'));
    syncFromServer();
  };

  const post = async (path, payload) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: payload instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload instanceof FormData ? payload : JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'The cart could not be updated.');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const refresh = async () => {
    const ids = sectionIds();
    if (!ids.length) return;
    const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    if (response.ok) render(await response.json());
  };

  /* Drawer */
  let lastFocus = null;

  const open = (opener) => {
    const dialog = drawer();
    if (!dialog || dialog.open) return;
    lastFocus = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('cbi-cart-open');
    if (window.cbiLenis) window.cbiLenis.stop();
    /* The closed position (panel off screen, clear backdrop) is painted
     * first, then is-open slides the panel in. cbi-cart.css also gives the
     * open state an @starting-style, which covers the same entry. */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    });
  };

  const close = () => {
    const dialog = drawer();
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    const done = () => {
      dialog.close();
      document.documentElement.classList.remove('cbi-cart-open');
      if (window.cbiLenis && !document.documentElement.classList.contains('cbi-interiors-stage')) window.cbiLenis.start();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      done();
    } else {
      const panel = dialog.querySelector('.cbi-cart-drawer__panel');
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        done();
      };
      panel.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, 600);
    }
  };

  /* Add to cart */
  const onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action) || !useDrawer()) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const opener = form.parentElement.querySelector('[data-cbi-cart-open]') || button;
    const message = form.parentElement.querySelector('[data-cbi-cart-error]');
    if (message) message.hidden = true;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const data = new FormData(form);
    const ids = sectionIds();
    if (ids.length) {
      data.append('sections', ids.join(','));
      data.append('sections_url', window.location.pathname);
    }

    try {
      const result = await post('cart/add.js', data);
      render(result.sections);
      /* cart/add.js answers with the line as it now stands, so the buy area
       * can settle before announce() goes back for the whole cart. */
      const buy = form.closest('[data-cbi-variant]');
      if (buy) setRemaining(buy, Math.max(Number(buy.dataset.cbiStock || 0) - (result.quantity || 1), 0), true);
      announce();
      open(opener);
      say(`${result.product_title || result.title || 'Piece'} added to cart`);
    } catch (error) {
      if (message) {
        message.textContent = error.message;
        message.hidden = false;
      }
      say(error.message);
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  };

  /* Remove */
  const remove = async (link) => {
    const line = link.closest('[data-cbi-cart-line]');
    const title = link.dataset.cbiCartTitle || 'Piece';
    if (line) line.classList.add('is-removing');
    try {
      const result = await post('cart/change.js', {
        id: link.dataset.cbiCartRemove,
        quantity: 0,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      });
      render(result.sections);
      announce();
      say(`${title} removed from cart`);
    } catch (error) {
      window.location.href = link.href;
    }
  };

  /* Cart line stepper. Each end is a link to the cart change URL, so this is
   * the same move the browser would make on its own, without the page load.
   * Focus follows the button through the re-render. */
  const setQuantity = async (link) => {
    const line = link.closest('[data-cbi-cart-line]');
    const key = link.dataset.cbiQtySet;
    const dir = link.dataset.cbiQtyDir;
    const title = line ? line.querySelector('.cbi-cart-line__title a')?.textContent.trim() : '';
    const wanted = Number(link.dataset.cbiQtyTo);
    const hadFocus = document.activeElement === link;
    if (line) line.classList.add('is-busy');
    try {
      const result = await post('cart/change.js', {
        id: key,
        quantity: wanted,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      });
      render(result.sections);
      announce();
      if (hadFocus) {
        const fresh = document.querySelector(`[data-cbi-cart-line="${CSS.escape(key)}"] [data-cbi-qty-dir="${dir}"]`);
        const other = document.querySelector(`[data-cbi-cart-line="${CSS.escape(key)}"] [data-cbi-qty-dir]`);
        (fresh || other)?.focus();
      }
      say(`${title || 'Piece'}, quantity ${wanted}`);
    } catch (error) {
      window.location.href = link.href;
    }
  };

  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    /* Product page stepper: nothing leaves the page, the form carries the
     * number when Add to cart is pressed. */
    const step = target.closest('[data-cbi-qty-step]');
    if (step) {
      event.preventDefault();
      const stepper = step.closest('[data-cbi-qty]');
      const input = stepper && stepper.querySelector('.cbi-qty__input');
      if (input) {
        const value = Number(input.value || 1) + Number(step.dataset.cbiQtyStep);
        input.value = String(Math.min(Math.max(value, Number(input.min || 1)), Number(input.max || 99)));
        syncSteps(stepper);
      }
      return;
    }

    const qtyLink = target.closest('[data-cbi-qty-set]');
    if (qtyLink) {
      event.preventDefault();
      setQuantity(qtyLink);
      return;
    }

    const removeLink = target.closest('[data-cbi-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      remove(removeLink);
      return;
    }

    if (target.closest('[data-cbi-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    /* A click on the dialog itself, outside the panel, is a click on the
     * backdrop. */
    const dialog = drawer();
    if (dialog && target === dialog) {
      close();
      return;
    }

    const cartLink = target.closest('a.cbi-header__cart, [data-cbi-cart-open]');
    if (cartLink && useDrawer() && !onCartPage() && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      refresh();
      open(cartLink);
    }
  };

  /* Order note: saved as it is typed, and on the way to checkout by the form
   * itself. */
  let noteTimer = null;
  const onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLTextAreaElement) || !field.matches('[data-cbi-cart-note]')) return;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(() => {
      post('cart/update.js', { note: field.value }).catch(() => {});
    }, 400);
  };

  /* A typed number is held to the range the same way. */
  const onQtyInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || !field.matches('.cbi-qty__input')) return;
    const stepper = field.closest('[data-cbi-qty]');
    if (stepper) syncSteps(stepper);
  };

  for (const stepper of document.querySelectorAll('[data-cbi-qty]')) syncSteps(stepper);

  document.addEventListener('submit', onSubmit);
  document.addEventListener('input', onQtyInput);
  document.addEventListener('click', onClick);
  document.addEventListener('input', onInput);
  document.addEventListener('cancel', (event) => {
    if (event.target === drawer()) {
      event.preventDefault();
      close();
    }
  }, true);

  /* Back and forward serve the page as it was, from the back forward cache
   * or the HTTP cache, which means a cart and a buy area rendered against a
   * cart that has since changed: step a line down on the cart page, go back,
   * and the piece would still read In cart. The header watches pageshow for
   * its own count, so this only has the cart and the buy areas to settle. */
  window.addEventListener('pageshow', (event) => {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!event.persisted && (!navigation || navigation.type !== 'back_forward')) return;
    refresh();
    syncFromServer();
  });

  /* Theme editor: preview the drawer while its section is selected. */
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail && event.detail.sectionId === 'cbi-cart-drawer') open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail && event.detail.sectionId === 'cbi-cart-drawer') close();
  });

  window.cbiCart = { open, close, refresh };
})();
